import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { usePlatform } from "../../hooks/usePlatform";
import { useAuth } from "../../context/authContext";
import LogoutConfirmModal from "../logout-confirm-modal";
import styles from "./style.module.css";

/**
 * Кнопка-действие в шапке web-версии (TG сам рисует свою `tg.BackButton`,
 * поэтому здесь компонент не рендерится).
 *
 *  - На главной (`/`) — «Выйти»: на главном экране возвращаться некуда,
 *    логично показать выход вместо «Назад» в том же месте.
 *  - На `/select-tariff` — «‹ Назад» на главную (`/`).
 *  - На остальных страницах — «‹ Назад» (history.back).
 *  - На `/login` — не рендерится: страница и так пред-аутентификационная.
 *
 * Расположена fixed'ом в левом верхнем углу `.app-frame`.
 */
function WebBackButton() {
  const { isTelegram } = usePlatform();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  if (isTelegram) return null;
  if (location.pathname === "/login" || location.pathname.startsWith("/login/")) {
    return null;
  }

  /** На документах своя кнопка в потоке скролла (DocumentPage). */
  const isDocumentLikePage = /^\/(privacy-policy|user-agreement|agreement|offer|history|devices|faq(\/.+)?)$/.test(
    location.pathname
  );
  const isInstructionPage = location.pathname === "/instruction";
  if (isDocumentLikePage || isInstructionPage) return null;

  const isRoot = location.pathname === "/";
  const isSelectTariff = location.pathname === "/select-tariff";

  const handleBackClick = () => {
    if (isSelectTariff) {
      navigate("/", { replace: true });
      return;
    }
    navigate(-1);
  };

  const handleLogoutClick = () => {
    if (!user || isLoggingOut) return;
    setLogoutModalOpen(true);
  };

  const handleLogoutConfirm = async () => {
    if (!user || isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
      setLogoutModalOpen(false);
      navigate("/login", { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const label = isRoot ? "Выйти" : "Назад";

  return (
    <>
      <div className={styles.backBar}>
        <div className={styles.backBarInner}>
          <button
            type="button"
            className={styles.backButton}
            onClick={isRoot ? handleLogoutClick : handleBackClick}
            disabled={isRoot && isLoggingOut}
            aria-label={label}
          >
            {!isRoot && (
              <span className={styles.chevron} aria-hidden>
                &#8249;
              </span>
            )}
            <span>{label}</span>
          </button>
        </div>
      </div>

      {isRoot && (
        <LogoutConfirmModal
          open={logoutModalOpen}
          onClose={() => setLogoutModalOpen(false)}
          onConfirm={() => void handleLogoutConfirm()}
          loading={isLoggingOut}
        />
      )}
    </>
  );
}

export default WebBackButton;
